import {
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Box, useBreakpointValue, useHoverable } from "@gemini/core";
import { Button } from "@gemini/ui";
import { CloseIcon } from "@gemini/icons";
import { useBrand } from "@aviv-unified-frontend/runtime/brand";

import { STATIC_ASSETS_PREFIX } from "../../../../constants/assets";
import { useTranslate } from "../../../../utils/useTranslate";
import { useCustomBannerTracking } from "../useCustomBannerTracking";

import type { CustomBannerProps } from "./types";

// eslint-disable-next-line sonarjs/cognitive-complexity
export const PersistentCustomBanner = ({
  bannerInfo,
  isBannerVisible,
  onClick,
  onDismiss,
}: CustomBannerProps) => {
  const t = useTranslate();
  const { id: brandId } = useBrand();
  const [isBannerOpen, setIsBannerOpen] = useState(isBannerVisible);
  const { onDisplayEvent } = useCustomBannerTracking();
  const textRef = useRef<HTMLDivElement | null>(null);
  const { boxProps: hoverableProps, isHovered } = useHoverable();

  const isMobileOrTablet = useBreakpointValue({
    base: true,
    xl: false,
  });

  const [qrCodeSize, setQrCodeSize] = useState<string>("110px");
  const onDisplayEventStable = useEffectEvent(onDisplayEvent);
  const hasOpenedOnce = useRef(false);

  /**
   * Open the banner without user interaction only on first render
   */
  useEffect(() => {
    if (isBannerVisible && !hasOpenedOnce.current) {
      setIsBannerOpen(true);
      hasOpenedOnce.current = true;
    }
  }, [isBannerVisible]);

  useEffect(() => {
    if (!isBannerOpen) return;

    onDisplayEventStable();
  }, [isBannerOpen]);

  /**
   * Resize the QR code based on the height of the text container
   * to keep it square and aligned with the text height.
   *
   * This effect is only applied on larger viewports where the QR code is visible.
   */
  useLayoutEffect(() => {
    const textEl = textRef.current;

    if (!textEl || !("ResizeObserver" in window)) return;

    let frameId: number | null = null;

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;

      const height = entry.contentRect.height;

      if (!height) return;

      const nextSize = `${height}px`;

      // Defer to the next frame to avoid ResizeObserver loop warning
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        setQrCodeSize((prev) => (prev === nextSize ? prev : nextSize));
      });
    });

    observer.observe(textEl);

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      observer.disconnect();
    };
  }, [isMobileOrTablet, isBannerOpen]);

  const { qrcode, translation, storeUrl } = bannerInfo;
  const { buttonVariant, containerStyles } = getCustomStyles(brandId);
  const title = t(`customAppBanner.${translation}.mobile.title`);
  const description = t(`customAppBanner.${translation}.mobile.description`);
  const qrCodeSrc = `${STATIC_ASSETS_PREFIX}/custom-banner/${qrcode}`;

  return (
    <>
      {/* Small QR Code only box when collapsed */}
      <Box
        /**
         * This custom attribute is used by React Aria to identify
         * top layers in the application (like modals, banners, etc.).
         *
         * See https://github.com/adobe/react-spectrum/issues/8784#issuecomment-3415368636
         */
        data-react-aria-top-layer="true"
        as="button"
        {...containerStyles}
        width="86px"
        height="86px"
        cursor="pointer"
        visibility={isBannerOpen ? "hidden" : "visible"}
        opacity={isBannerOpen ? "opacity.none" : "opacity.full"}
        padding="spacing.8"
        backgroundColor={
          isHovered
            ? "color.surface.brand.primary.hover"
            : "color.surface.brand.primary.default"
        }
        onClick={() => setIsBannerOpen(true)}
        {...hoverableProps}
      >
        <Box
          as="img"
          backgroundColor="color.background.constant.white"
          borderRadius="radius.8"
          padding="spacing.4"
          src={qrCodeSrc}
          alt="QR Code Application"
          draggable={false}
        />
      </Box>

      {/* Expanded Banner box when open */}
      <Box
        /**
         * This custom attribute is used by React Aria to identify
         * top layers in the application (like modals, banners, etc.).
         *
         * See https://github.com/adobe/react-spectrum/issues/8784#issuecomment-3415368636
         */
        data-react-aria-top-layer="true"
        {...containerStyles}
        display="flex"
        padding="spacing.16"
        paddingRight="spacing.40"
        justifyContent="center"
        alignItems="center"
        gap="spacing.16"
        overflow="hidden"
        maxWidth="42.5rem"
        height="fit-content"
        backgroundColor="color.surface.brand.primary.default"
        visibility={isBannerOpen ? "visible" : "hidden"}
        opacity={isBannerOpen ? "opacity.full" : "opacity.none"}
      >
        <Box
          position="absolute"
          top="0"
          right="0"
          padding="spacing.4"
        >
          <Button
            size="32"
            icon={CloseIcon}
            iconOnly
            onPress={() => {
              onDismiss(Boolean(isBannerVisible));
              setIsBannerOpen(false);
            }}
            type="button"
            variant={buttonVariant}
          >
            Close
          </Button>
        </Box>
        <Box
          as="img"
          backgroundColor="color.background.constant.white"
          borderRadius="radius.8"
          padding="spacing.4"
          src={qrCodeSrc}
          alt="QR Code Application"
          width={qrCodeSize}
          height={qrCodeSize}
          maxHeight="204px" // Setting max height (12.75rem) to prevent oversized QR codes and size shifting
          maxWidth="204px" // Setting max width (12.75rem) to prevent oversized QR codes and size shifting
          draggable={false}
        />
        <Box
          ref={textRef}
          flexDirection="column"
          justifyContent="space-between"
          width="100%"
        >
          <Box
            paddingBottom="spacing.8"
            typography="typography.headline.20.bold"
          >
            {title}
          </Box>
          <Box
            paddingBottom="spacing.8"
            typography="typography.body.14.regular"
          >
            {description}
          </Box>

          <Box
            display="flex"
            gap="spacing.16"
          >
            <StoreBadge
              type="ios"
              url={storeUrl}
              onClick={() => {
                onClick("App Store");
              }}
              height="spacing.32"
              isClickable={isMobileOrTablet}
            />
            <StoreBadge
              type="android"
              url={storeUrl}
              onClick={() => {
                onClick("Google Play");
              }}
              height="spacing.32"
              isClickable={isMobileOrTablet}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

const StoreBadgeContent = {
  android: {
    imgSrc: `${STATIC_ASSETS_PREFIX}/custom-banner/store-android.svg`,
    alt: "Google Play Store",
  },
  ios: {
    imgSrc: `${STATIC_ASSETS_PREFIX}/custom-banner/store-ios.svg`,
    alt: "App Store",
  },
};

const StoreBadge = ({
  type,
  url,
  isClickable,
  onClick,
  height,
}: {
  type: "android" | "ios";
  url: string;
  isClickable: boolean;
  onClick: () => void;
  height: string;
}) => {
  const { imgSrc, alt } = StoreBadgeContent[type];

  const content = (
    <Box
      as="img"
      src={imgSrc}
      alt={alt}
      height={height}
    />
  );

  if (!isClickable) {
    return content;
  }

  return (
    <Box
      as="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
    >
      {content}
    </Box>
  );
};

const getCustomStyles = (brandId: string) => {
  const color =
    brandId === "sl"
      ? ("color.content.constant.white.default" as const)
      : ("color.content.active.default" as const);

  const buttonVariant =
    brandId === "sl" ? ("tertiary-inverted" as const) : ("tertiary" as const);

  const containerStyles = {
    position: "fixed" as const,
    bottom: "spacing.24" as const,
    right: "spacing.24" as const,
    zIndex: "zIndex.4" as const,
    overflow: "hidden" as const,
    pointerEvents: "all" as const,
    borderColor: "color.content.interactive.inverted.default" as const,
    borderTopStyle: "solid" as const,
    borderTopWidth: "borderWidth.1" as const,
    borderBottomWidth: "borderWidth.none" as const,
    borderLeftWidth: "borderWidth.none" as const,
    borderRightWidth: "borderWidth.none" as const,
    borderRadius: "radius.16" as const,
    boxShadow: "shadow.16" as const,
    color,
    transitionDuration: "motionDuration.150" as const,
    transitionTimingFunction: "motionEasing.ease-out" as const,
    transitionProperty: "opacity, visibility" as const,
  };

  return {
    containerStyles,
    buttonVariant,
  };
};
